import {
  AppAccountStateFrameSchema,
  AppSocketHelloSchema,
  AppSocketServerFrameSchema,
  parseIJson,
  type AppSocketServerFrame,
} from "@mind/contracts/browser";
import type { QueryClient } from "@tanstack/react-query";

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 250;
const MAX_RECONNECT_EXPONENT = 16;

const AUTH_CLOSE_CODES: Readonly<Partial<Record<number, true>>> = {
  4401: true,
  4403: true,
};
const TERMINAL_CLOSE_CODES: Readonly<Partial<Record<number, true>>> = {
  1000: true,
  1002: true,
  1003: true,
  1007: true,
  1008: true,
  1009: true,
  4401: true,
  4403: true,
};

export type AppRealtimeConnection = "idle" | "connecting" | "connected" | "reconnecting" | "stopped";

export type AppRealtimeWorkspaceCursor = {
  readonly workspaceId: string;
  readonly lastSequence: string;
};
export type AppRealtimeHello = {
  readonly type: "HELLO";
  readonly workspaces: readonly AppRealtimeWorkspaceCursor[];
};


export type AppRealtimeAccountState = {
  readonly actorAccountId: string;
  readonly effectiveAccountId: string;
  readonly simulationSessionId: string | null;
  readonly simulated: boolean;
};

export type AppRealtimeSnapshot = {
  readonly connection: AppRealtimeConnection;
  readonly accountState: AppRealtimeAccountState | null;
  readonly cursors: readonly AppRealtimeWorkspaceCursor[];
  readonly generation: number;
};

export type AppRealtimeContext = {
  readonly actorAccountId: string;
  readonly effectiveAccountId: string;
  readonly simulationSessionId: string | null;
  readonly workspaces: readonly AppRealtimeWorkspaceCursor[];
};

export type AppRealtimeSocketHandlers = {
  readonly open: () => void;
  readonly message: (data: unknown) => void;
  readonly close: (code: number) => void;
  readonly error: () => void;
};

export type AppRealtimeSocket = {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  listen(handlers: AppRealtimeSocketHandlers): () => void;
};

export type AppRealtimeStoreOptions = {
  readonly queryClient: QueryClient;
  readonly onIdentityMismatch?: (received: AppRealtimeAccountState) => void;
  readonly socketFactory?: (url: string) => AppRealtimeSocket;
  readonly random?: () => number;
};

type ParsedContext = {
  readonly identity: AppRealtimeAccountState;
  readonly cursors: ReadonlyMap<string, string>;
};

type EventQueryScope = {
  readonly roots: ReadonlySet<string>;
  readonly broad: boolean;
};

function createBrowserSocket(url: string): AppRealtimeSocket {
  const socket = new WebSocket(url);
  socket.binaryType = "arraybuffer";
  return {
    send(data) {
      socket.send(data);
    },
    close(code, reason) {
      socket.close(code, reason);
    },
    listen(handlers) {
      const onOpen = (): void => handlers.open();
      const onMessage = (event: MessageEvent<unknown>): void => handlers.message(event.data);
      const onClose = (event: CloseEvent): void => handlers.close(event.code);
      const onError = (): void => handlers.error();
      socket.addEventListener("open", onOpen);
      socket.addEventListener("message", onMessage);
      socket.addEventListener("close", onClose);
      socket.addEventListener("error", onError);
      return () => {
        socket.removeEventListener("open", onOpen);
        socket.removeEventListener("message", onMessage);
        socket.removeEventListener("close", onClose);
        socket.removeEventListener("error", onError);
      };
    },
  };
}

function appSocketUrl(): string {
  const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
  return new URL("/ws/app", `${protocol}//${globalThis.location.host}`).href;
}

function parseContext(context: AppRealtimeContext): ParsedContext {
  const identityFrame = AppAccountStateFrameSchema.parse({
    type: "ACCOUNT_STATE",
    actorAccountId: context.actorAccountId,
    effectiveAccountId: context.effectiveAccountId,
    simulationSessionId: context.simulationSessionId,
    simulated: context.simulationSessionId !== null,
  });
  const hello = AppSocketHelloSchema.parse({
    type: "HELLO",
    workspaces: context.workspaces,
  });
  return {
    identity: Object.freeze({
      actorAccountId: identityFrame.actorAccountId,
      effectiveAccountId: identityFrame.effectiveAccountId,
      simulationSessionId: identityFrame.simulationSessionId,
      simulated: identityFrame.simulated,
    }),
    cursors: new Map(hello.workspaces.map((cursor) => [cursor.workspaceId, cursor.lastSequence])),
  };
}

function sameIdentity(left: AppRealtimeAccountState, right: AppRealtimeAccountState): boolean {
  return left.actorAccountId === right.actorAccountId
    && left.effectiveAccountId === right.effectiveAccountId
    && left.simulationSessionId === right.simulationSessionId
    && left.simulated === right.simulated;
}

function sameCursors(left: ReadonlyMap<string, string>, right: ReadonlyMap<string, string>): boolean {
  if (left.size !== right.size) return false;
  for (const [workspaceId, sequence] of left) {
    if (right.get(workspaceId) !== sequence) return false;
  }
  return true;
}

function mergeCurrentCursors(
  current: ReadonlyMap<string, string>,
  incoming: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> {
  const merged = new Map<string, string>();
  for (const [workspaceId, incomingSequence] of incoming) {
    const currentSequence = current.get(workspaceId);
    merged.set(
      workspaceId,
      currentSequence !== undefined && BigInt(currentSequence) > BigInt(incomingSequence)
        ? currentSequence
        : incomingSequence,
    );
  }
  return merged;
}

function parseFrameData(data: unknown): AppSocketServerFrame | undefined {
  let decoded: unknown;
  try {
    if (typeof data === "string" || data instanceof Uint8Array) {
      decoded = parseIJson(data);
    } else if (data instanceof ArrayBuffer) {
      decoded = parseIJson(new Uint8Array(data));
    } else {
      return undefined;
    }
  } catch {
    return undefined;
  }
  const result = AppSocketServerFrameSchema.safeParse(decoded);
  return result.success ? result.data : undefined;
}

function eventQueryScope(eventTypes: ReadonlySet<string>): EventQueryScope {
  const roots = new Set<string>();
  let broad = false;
  for (const eventType of eventTypes) {
    const domain = eventType.split(".", 1)[0];
    switch (domain) {
      case "project":
      case "task":
        roots.add("work");
        roots.add("graph");
        break;
      case "focus":
        roots.add("focus");
        roots.add("graph");
        break;
      case "document":
        roots.add("documents");
        roots.add("graph");
        break;
      case "proposal":
        roots.add("documents");
        break;
      case "note":
        roots.add("focus");
        roots.add("graph");
        break;
      case "entity_link":
        roots.add("graph");
        break;
      default:
        broad = true;
        break;
    }
  }
  return { roots, broad };
}

export class AppRealtimeStore {
  private readonly queryClient: QueryClient;
  private readonly onIdentityMismatch: ((received: AppRealtimeAccountState) => void) | undefined;
  private readonly socketFactory: (url: string) => AppRealtimeSocket;
  private readonly random: () => number;
  private readonly listeners = new Set<() => void>();
  private cursors: ReadonlyMap<string, string> = new Map();
  private expectedIdentity: AppRealtimeAccountState | null = null;
  private accountState: AppRealtimeAccountState | null = null;
  private connection: AppRealtimeConnection = "idle";
  private generation = 0;
  private snapshot: AppRealtimeSnapshot;
  private socket: AppRealtimeSocket | null = null;
  private detachSocket: (() => void) | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private wantsConnection = false;
  private disposed = false;

  public constructor(options: AppRealtimeStoreOptions) {
    this.queryClient = options.queryClient;
    this.onIdentityMismatch = options.onIdentityMismatch;
    this.socketFactory = options.socketFactory ?? createBrowserSocket;
    this.random = options.random ?? Math.random;
    this.snapshot = this.buildSnapshot();
  }

  public readonly subscribe = (listener: () => void): (() => void) => {
    if (this.disposed) return () => undefined;
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  public readonly getSnapshot = (): AppRealtimeSnapshot => this.snapshot;

  public getHello(): AppRealtimeHello {
    return AppSocketHelloSchema.parse({
      type: "HELLO",
      workspaces: this.snapshot.cursors,
    });
  }

  public updateContext(context: AppRealtimeContext): void {
    if (this.disposed) throw new Error("App realtime store is disposed");
    const parsed = parseContext(context);
    const identityChanged = this.expectedIdentity !== null
      && !sameIdentity(this.expectedIdentity, parsed.identity);
    const nextCursors = identityChanged
      ? parsed.cursors
      : mergeCurrentCursors(this.cursors, parsed.cursors);
    const cursorsChanged = !sameCursors(this.cursors, nextCursors);

    if (identityChanged) {
      const reconnect = this.wantsConnection;
      this.cancelReconnect();
      this.closeSocket();
      this.queryClient.clear();
      this.generation += 1;
      this.accountState = null;
      this.expectedIdentity = parsed.identity;
      this.cursors = nextCursors;
      this.connection = reconnect ? "connecting" : "stopped";
      this.publish();
      if (reconnect) this.openSocket(false);
      return;
    }

    this.expectedIdentity = parsed.identity;
    if (!cursorsChanged) return;
    this.cursors = nextCursors;
    this.publish();
    if (this.wantsConnection) this.restartSocket();
  }

  public connect(): void {
    if (this.disposed || this.expectedIdentity === null) return;
    this.wantsConnection = true;
    if (this.socket !== null || this.reconnectTimer !== null) return;
    this.openSocket(false);
  }

  public disconnect(): void {
    if (this.disposed) return;
    const hadSensitiveState = this.expectedIdentity !== null
      || this.accountState !== null
      || this.cursors.size > 0;
    this.wantsConnection = false;
    this.cancelReconnect();
    this.closeSocket();
    this.queryClient.clear();
    this.expectedIdentity = null;
    this.accountState = null;
    this.cursors = new Map();
    this.reconnectAttempt = 0;
    if (hadSensitiveState) this.generation += 1;
    this.connection = "stopped";
    this.publish();
  }

  public dispose(): void {
    if (this.disposed) return;
    const hadSensitiveState = this.expectedIdentity !== null
      || this.accountState !== null
      || this.cursors.size > 0;
    this.disposed = true;
    this.wantsConnection = false;
    this.cancelReconnect();
    this.closeSocket();
    this.queryClient.clear();
    this.expectedIdentity = null;
    this.accountState = null;
    this.cursors = new Map();
    if (hadSensitiveState) this.generation += 1;
    this.connection = "stopped";
    this.publish();
    this.listeners.clear();
  }

  private buildSnapshot(): AppRealtimeSnapshot {
    const cursors = Object.freeze(Array.from(this.cursors, ([workspaceId, lastSequence]) => Object.freeze({
      workspaceId,
      lastSequence,
    })));
    return Object.freeze({
      connection: this.connection,
      accountState: this.accountState,
      cursors,
      generation: this.generation,
    });
  }

  private publish(): void {
    this.snapshot = this.buildSnapshot();
    for (const listener of this.listeners) listener();
  }

  private setConnection(connection: AppRealtimeConnection): void {
    if (this.connection === connection) return;
    this.connection = connection;
    this.publish();
  }

  private openSocket(reconnecting: boolean): void {
    if (this.disposed || !this.wantsConnection || this.expectedIdentity === null || this.socket !== null) return;
    const nextConnection = reconnecting ? "reconnecting" : "connecting";
    const snapshotChanged = this.accountState !== null || this.connection !== nextConnection;
    this.accountState = null;
    this.connection = nextConnection;
    if (snapshotChanged) this.publish();
    let socket: AppRealtimeSocket;
    try {
      socket = this.socketFactory(appSocketUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    this.detachSocket = socket.listen({
      open: () => this.handleOpen(socket),
      message: (data) => this.handleMessage(socket, data),
      close: (code) => this.handleClose(socket, code),
      error: () => undefined,
    });
  }

  private handleOpen(socket: AppRealtimeSocket): void {
    if (this.socket !== socket || !this.wantsConnection) return;
    this.setConnection("connected");
    try {
      socket.send(JSON.stringify(this.getHello()));
    } catch {
      this.dropSocket(socket, 1011, "send failed");
      this.scheduleReconnect();
    }
  }

  private handleMessage(socket: AppRealtimeSocket, data: unknown): void {
    if (this.socket !== socket) return;
    const frame = parseFrameData(data);
    if (frame === undefined) return;
    switch (frame.type) {
      case "ACCOUNT_STATE":
        this.handleAccountState(frame);
        break;
      case "EVENT_BATCH":
        if (this.accountState !== null) this.handleEventBatch(frame);
        break;
      case "INVALIDATE":
        if (this.accountState !== null) this.handleInvalidation(frame.workspaceId, frame.resumeSequence);
        break;
    }
  }

  private handleAccountState(frame: Extract<AppSocketServerFrame, { type: "ACCOUNT_STATE" }>): void {
    if (frame.simulated !== (frame.simulationSessionId !== null)) return;
    const received = Object.freeze({
      actorAccountId: frame.actorAccountId,
      effectiveAccountId: frame.effectiveAccountId,
      simulationSessionId: frame.simulationSessionId,
      simulated: frame.simulated,
    });
    if (this.expectedIdentity === null || !sameIdentity(this.expectedIdentity, received)) {
      this.teardownForIdentityMismatch(received);
      return;
    }
    if (this.accountState !== null && sameIdentity(this.accountState, received)) {
      this.reconnectAttempt = 0;
      return;
    }
    this.accountState = received;
    this.reconnectAttempt = 0;
    this.publish();
  }

  private handleEventBatch(frame: Extract<AppSocketServerFrame, { type: "EVENT_BATCH" }>): void {
    const currentSequence = this.cursors.get(frame.workspaceId);
    if (currentSequence === undefined) return;
    const current = BigInt(currentSequence);
    let expected = current + 1n;
    const eventTypes = new Set<string>();
    let hasNewEvents = false;

    for (const event of frame.events) {
      const sequence = BigInt(event.sequence);
      if (sequence <= current) continue;
      if (sequence !== expected) return;
      expected += 1n;
      hasNewEvents = true;
      eventTypes.add(event.type);
    }
    if (!hasNewEvents) return;
    if (BigInt(frame.nextSequence) !== expected - 1n) return;

    const nextCursors = new Map(this.cursors);
    nextCursors.set(frame.workspaceId, frame.nextSequence);
    this.cursors = nextCursors;
    this.publish();
    this.invalidateEventQueries(frame.workspaceId, eventTypes);
  }

  private handleInvalidation(workspaceId: string, resumeSequence: string): void {
    if (!this.cursors.has(workspaceId)) return;
    const nextCursors = new Map(this.cursors);
    nextCursors.set(workspaceId, resumeSequence);
    this.cursors = nextCursors;
    this.publish();
    void this.queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.some((part) => part === workspaceId),
    });
  }

  private invalidateEventQueries(workspaceId: string, eventTypes: ReadonlySet<string>): void {
    const scope = eventQueryScope(eventTypes);
    void this.queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.some((part) => part === workspaceId)
        && (scope.broad || (typeof query.queryKey[0] === "string" && scope.roots.has(query.queryKey[0]))),
    });
  }

  private handleClose(socket: AppRealtimeSocket, code: number): void {
    if (this.socket !== socket) return;
    this.detachCurrentSocket();
    this.socket = null;
    if (!this.wantsConnection || TERMINAL_CLOSE_CODES[code] === true) {
      this.wantsConnection = false;
      this.cancelReconnect();
      if (AUTH_CLOSE_CODES[code] === true) {
        const lostIdentity = this.accountState ?? this.expectedIdentity;
        this.queryClient.clear();
        this.expectedIdentity = null;
        this.accountState = null;
        this.cursors = new Map();
        this.generation += 1;
        this.connection = "stopped";
        this.publish();
        if (lostIdentity !== null) {
          try {
            this.onIdentityMismatch?.(lostIdentity);
          } catch {
            // Authentication-loss teardown cannot depend on application notification code.
          }
        }
      } else {
        this.setConnection("stopped");
      }
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.disposed || !this.wantsConnection || this.reconnectTimer !== null) return;
    this.setConnection("reconnecting");
    const exponent = Math.min(this.reconnectAttempt, MAX_RECONNECT_EXPONENT);
    this.reconnectAttempt = Math.min(this.reconnectAttempt + 1, MAX_RECONNECT_EXPONENT);
    const exponentialDelay = Math.min(
      MAX_RECONNECT_DELAY_MS,
      INITIAL_RECONNECT_DELAY_MS * (2 ** exponent),
    );
    const randomSample = Math.max(0, Math.min(1, this.random()));
    const delay = Math.min(
      MAX_RECONNECT_DELAY_MS,
      Math.round(exponentialDelay * (0.75 + randomSample * 0.5)),
    );
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket(true);
    }, delay);
  }

  private restartSocket(): void {
    this.cancelReconnect();
    this.closeSocket();
    this.openSocket(false);
  }

  private teardownForIdentityMismatch(received: AppRealtimeAccountState): void {
    this.wantsConnection = false;
    this.cancelReconnect();
    this.closeSocket(1000, "identity changed");
    this.queryClient.clear();
    this.expectedIdentity = null;
    this.accountState = null;
    this.cursors = new Map();
    this.generation += 1;
    this.connection = "stopped";
    this.publish();
    try {
      this.onIdentityMismatch?.(received);
    } catch {
      // Store teardown must not depend on application notification code.
    }
  }

  private dropSocket(socket: AppRealtimeSocket, code: number, reason: string): void {
    if (this.socket !== socket) return;
    this.detachCurrentSocket();
    this.socket = null;
    socket.close(code, reason);
  }

  private closeSocket(code = 1000, reason = "client disconnect"): void {
    const socket = this.socket;
    if (socket === null) return;
    this.detachCurrentSocket();
    this.socket = null;
    socket.close(code, reason);
  }

  private detachCurrentSocket(): void {
    this.detachSocket?.();
    this.detachSocket = null;
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer === null) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}

export function createAppRealtimeStore(options: AppRealtimeStoreOptions): AppRealtimeStore {
  return new AppRealtimeStore(options);
}
